import { ClusterType } from "@/types";

const MAX_SCROLL_ATTEMPTS = 30;
const SCROLL_RETRY_MS = 100;
const DEFAULT_GRID_GAP_PX = 16;
const DEFAULT_GRID_PADDING_TOP_PX = 8;

function getScrollParent(element: HTMLElement): HTMLElement {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }

  return document.documentElement;
}

function getGridSpacing(): { gap: number; paddingTop: number } {
  const gridContainer = document.getElementById("hardware-grid-container");
  if (!gridContainer) {
    return { gap: DEFAULT_GRID_GAP_PX, paddingTop: DEFAULT_GRID_PADDING_TOP_PX };
  }

  const styles = window.getComputedStyle(gridContainer);
  const gap = parseFloat(styles.rowGap || styles.gap || "") || DEFAULT_GRID_GAP_PX;
  const paddingTop = parseFloat(styles.paddingTop || "") || DEFAULT_GRID_PADDING_TOP_PX;

  return { gap, paddingTop };
}

function getStickyHeaderOffset(scrollParent: HTMLElement): number {
  const stickyHeader =
    scrollParent.querySelector<HTMLElement>("[data-sticky-stack]") ||
    scrollParent.querySelector<HTMLElement>(".sticky");

  return stickyHeader?.getBoundingClientRect().height ?? 0;
}

function isFirstGridRow(card: HTMLElement, grid: HTMLElement): boolean {
  const firstCard = grid.querySelector('[id^="hardware-card-"]');
  if (!(firstCard instanceof HTMLElement)) {
    return false;
  }

  return card.offsetTop <= firstCard.offsetTop + 1;
}

function getItemSpacingOffset(card: HTMLElement): number {
  const grid = document.getElementById("hardware-grid-container");
  const { gap, paddingTop } = getGridSpacing();

  if (!grid) {
    return gap;
  }

  return isFirstGridRow(card, grid) ? paddingTop : gap;
}

function scrollCardIntoView(
  hardwareCard: HTMLElement,
  scrollParent: HTMLElement,
  behavior: ScrollBehavior
) {
  const stickyOffset = getStickyHeaderOffset(scrollParent);
  const itemSpacingOffset = getItemSpacingOffset(hardwareCard);
  const parentRect = scrollParent.getBoundingClientRect();
  const cardRect = hardwareCard.getBoundingClientRect();
  const top =
    scrollParent.scrollTop +
    (cardRect.top - parentRect.top) -
    stickyOffset -
    itemSpacingOffset;

  scrollParent.scrollTo({
    top: Math.max(0, top),
    behavior
  });
}

export function scrollToHardwareCard(
  hardwareId: string,
  clusterType: ClusterType,
  options?: { behavior?: ScrollBehavior; onComplete?: (success: boolean) => void }
): boolean {
  const behavior = options?.behavior ?? "auto";
  const onComplete = options?.onComplete;

  const attemptScroll = (): boolean => {
    const hardwareCard = document.getElementById(`hardware-card-${hardwareId}`);
    if (!hardwareCard) {
      return false;
    }

    if (clusterType === ClusterType.VMAAS || clusterType === ClusterType.CONTAINER) {
      scrollCardIntoView(hardwareCard, getScrollParent(hardwareCard), behavior);
      return true;
    }

    const container = document.getElementById("hardware-grid-container");
    if (!container) {
      return false;
    }

    scrollCardIntoView(hardwareCard, container, behavior);
    return true;
  };

  if (attemptScroll()) {
    onComplete?.(true);
    return true;
  }

  let attempts = 0;
  const tryScroll = () => {
    if (attemptScroll()) {
      onComplete?.(true);
      return;
    }

    if (attempts >= MAX_SCROLL_ATTEMPTS) {
      onComplete?.(false);
      return;
    }

    attempts += 1;
    window.setTimeout(tryScroll, SCROLL_RETRY_MS);
  };

  window.requestAnimationFrame(tryScroll);
  return false;
}
